import { useState } from "react";
import Logo from "../../../general/Logo";
import FadeIn from "../../../general/FadeIn";
import RoundedInput from "../../../general/RoundedInput";
import OrangeButton from "../../../general/OrangeButton";
import Header from "../../header/Header";
import Api from "../../../../services/Api";

/**
 * RegisterPage — formulário de cadastro de novo usuário.
 * Quando `user` é fornecido (acesso via dropdown), exibe o Header da aplicação.
 * Quando acessado sem autenticação (link na LoginPage), exibe o layout centrado padrão.
 * @param {function} navigate  - redireciona para outra página
 * @param {object}   user      - usuário logado, se houver
 * @param {function} onLogout  - desloga o usuário
 */
function RegisterPage({ navigate, user, onLogout }) {
  const [form, setForm] = useState({
    nome: "",
    cpf: "",
    email: "",
    usuario: "",
    senha: "",
    confirmar: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, "") // remove tudo que não é número
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
      .slice(0, 14);
  };

  const update = (key) => (e) => {
    let value = e.target.value;

    if (key === "cpf") {
      value = formatCPF(value);
    }

    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    setError("");
    if (form.senha !== form.confirmar) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await Api.register({
        name: form.nome,
        cpf: form.cpf,
        email: form.email,
        username: form.usuario,
        password: form.senha,
      });
      navigate(user ? "search" : "login");
    } catch (e) {
      if (e.message === "Erro 429") {
        setError("Muitas requisições simultâneas, tente novamente mais tarde.");
      } else setError(e.message || "Erro ao cadastrar.");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: "nome", ph: "Nome*" },
    { key: "cpf", ph: "CPF *" },
    { key: "email", ph: "Email *", type: "email" },
    { key: "usuario", ph: "Usuário *" },
    { key: "senha", ph: "Senha *", type: "password" },
    { key: "confirmar", ph: "Repita a senha *", type: "password" },
  ];

  const form_content = (
    <div
      style={{
        width: "100%",
        maxWidth: 400,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {fields.map((f, i) => (
        <FadeIn key={f.key} delay={200 + i * 80}>
          <RoundedInput
            placeholder={f.ph}
            type={f.type || "text"}
            value={form[f.key]}
            onChange={update(f.key)}
          />
        </FadeIn>
      ))}

      <FadeIn delay={750}>
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <OrangeButton onClick={handleSubmit} disabled={loading}>
            {loading ? "Cadastrando..." : "Cadastrar"}
          </OrangeButton>
          {error && (
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#e53935",
                marginTop: 10,
              }}
            >
              {error}
            </p>
          )}
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#999",
              marginTop: 12,
            }}
          >
            (* Campos obrigatórios)
          </p>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              color: "#E8956A",
              marginTop: 16,
              cursor: "pointer",
            }}
            onClick={() => navigate(user ? "search" : "login")}
          >
            {user ? "Voltar para a busca" : "Já tem conta? Faça login"}
          </p>
        </div>
      </FadeIn>
    </div>
  );

  if (user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#fafafa",
        }}
      >
        <Header
          onHome={() => navigate("search")}
          onLogin={() => navigate("login")}
          user={user}
          onLogout={onLogout}
          navigate={navigate}
        />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <FadeIn delay={100}>
            <h1
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 32,
                fontWeight: 700,
                color: "#2d2d2d",
                textAlign: "center",
                marginBottom: 36,
              }}
            >
              Cadastrar Usuário
            </h1>
          </FadeIn>
          {form_content}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#fafafa",
        padding: 24,
      }}
    >
      <FadeIn>
        <div style={{ textAlign: "center", marginBottom: 40 }} onClick={() => navigate("search")}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Logo size="large" />
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={100}>
        <h1
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 32,
            fontWeight: 700,
            color: "#2d2d2d",
            textAlign: "center",
            marginBottom: 36,
          }}
        >
          Faça seu cadastro
        </h1>
      </FadeIn>

      {form_content}
    </div>
  );
}

export default RegisterPage;
